
import React from 'react';
import { ChildProps } from 'react-apollo';
import I18n from 'i18n-js';
import gql from 'graphql-tag';

import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import { PRICE_GUIDE_TOOL_CONDITIONS } from '@reverbdotcom/commons/src/condition_helpers';

import { SellFormPricingModal, PAGE_SIZE } from './SellFormPricingModal';

import {
  SearchSellFormLiveListingsTable,
} from '../../../../../schemas/rql';

const DEFAULT_OFFSET = 0;

interface IQueryVariableArgs {
  canonicalProductIds: string[];
  offset?: number;
}

export function queryVariables({
  canonicalProductIds,
  offset = DEFAULT_OFFSET,
}: IQueryVariableArgs) {
  return {
    canonicalProductIds,
    conditionSlugs: PRICE_GUIDE_TOOL_CONDITIONS,
    statuses: ['live'],
    itemRegion: 'US',
    limit: PAGE_SIZE,
    offset,
  };
}

interface IExternalProps {
  canonicalProductIds: string[];
}

type IProps = ChildProps<IExternalProps, SearchSellFormLiveListingsTable.Query>;

export function SellFormLiveListingsModalContainer({
  canonicalProductIds = [],
  data,
}: IProps) {
  const { listingsSearch = {} } = data ?? {};
  const {
    listings = [],
    total = 0,
    offset = 0,
  } = listingsSearch;

  if (!total) { return null; }

  const filteredPriceRecords = listings
    .filter(listing => listing.pricing?.buyerPrice?.display)
    .map(listing => ({
      timestamp: listing.publishedAt,
      conditionDisplayName: listing.condition.displayName,
      priceDisplay: listing.pricing?.buyerPrice?.display,
      id: listing._id,
    }));

  return (
    <SellFormPricingModal
      buttonText={I18n.t('discovery.sellForm.pricingSection.pricingTables.liveListings.buttonText')}
      headerTitle={I18n.t('discovery.sellForm.pricingSection.pricingTables.liveListings.modalHeader')}
      dateHeader={I18n.t('discovery.sellForm.pricingSection.pricingTables.liveListings.dateHeader')}
      priceHeader={I18n.t('discovery.sellForm.pricingSection.pricingTables.liveListings.priceHeader')}
      priceRecords={filteredPriceRecords}
      offset={offset}
      total={total}
      refetch={(newOffset) => {
        data.refetch({
          canonicalProductIds,
          offset: newOffset,
        });
      }}
    />
  );
}

export const LIVE_LISTINGS_QUERY_DEFINITION = gql`
  query Search_SellForm_LiveListingsTable(
    $canonicalProductIds: [String],
    $conditionSlugs: [String],
    $itemRegion: String,
    $limit: Int,
    $offset: Int
  ) {
    listingsSearch(
      input: {
        canonicalProductIds: $canonicalProductIds,
        conditionSlugs: $conditionSlugs,
        itemRegion: $itemRegion,
        statuses: ["live"],
        limit: $limit,
        offset: $offset
      }
    ) {
      total
      offset
      listings {
        _id
        publishedAt {
          seconds
        }
        condition {
          displayName
        }
        pricing {
          buyerPrice {
            display
          }
        }
      }
    }
  }
`;

const withLiveListingsTableQuery = withGraphql<IProps, SearchSellFormLiveListingsTable.Query>(
  LIVE_LISTINGS_QUERY_DEFINITION,
  {
    skip: ({ canonicalProductIds }) => !canonicalProductIds?.length,
    options: ({
      canonicalProductIds,
    }) => {
      return {
        ssr: false,
        errorPolicy: 'ignore',
        variables: queryVariables({
          canonicalProductIds,
        }),
      };
    },
  },
);

export default withLiveListingsTableQuery(props => <SellFormLiveListingsModalContainer {...props} />);
